import { sub, milliseconds, setMilliseconds, isBefore, isValid, type Duration } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { isLocalTimeZone, normalizeIANATimeZone } from './time-zone-utils';

/**
 * Represents an absolute time range with start and end dates.
 */
export interface AbsoluteTimeRange {
  start: Date;
  end: Date;
}

/**
 * Represents a relative time range with a past duration (e.g., "5m", "1h").
 * The end date defaults to the current time if not specified.
 */
export interface RelativeTimeRange {
  /** End date or undefined if relative to the current Date */
  end?: Date;
  pastDuration: DurationString;
}

/**
 * A time range value that can be either absolute or relative.
 */
export type TimeRangeValue = AbsoluteTimeRange | RelativeTimeRange;

/**
 * Duration unit constants used in duration strings.
 */
export enum DURATION_UNIT {
  MILLISECOND = 'ms',
  SECOND = 's',
  MINUTE = 'm',
  HOUR = 'h',
  DAY = 'd',
  WEEK = 'w',
  MONTH = 'M',
  YEAR = 'y',
}

type MillisecondsDurationString = `${number}${DURATION_UNIT.MILLISECOND}`;
type SecondsDurationString = `${number}${DURATION_UNIT.SECOND}`;
type MinutesDurationString = `${number}${DURATION_UNIT.MINUTE}`;
type HoursDurationString = `${number}${DURATION_UNIT.HOUR}`;
type DaysDurationString = `${number}${DURATION_UNIT.DAY}`;
type WeeksDurationString = `${number}${DURATION_UNIT.WEEK}`;
type MonthsDurationString = `${number}${DURATION_UNIT.MONTH}`;
type YearsDurationString = `${number}${DURATION_UNIT.YEAR}`;

/**
 * A type-safe duration string in the format "1y2M3w4d5h6m7s8ms".
 * Units must appear in order from largest to smallest (y, M, w, d, h, m, s, ms).
 */
export type DurationString = Exclude<
  `${YearsDurationString | ''}${MonthsDurationString | ''}${WeeksDurationString | ''}${DaysDurationString | ''}${HoursDurationString | ''}${
    | MinutesDurationString
    | ''}${SecondsDurationString | ''}${MillisecondsDurationString | ''}`,
  ''
>;

/**
 * Regular expression pattern for parsing duration strings.
 * The pattern matches strings in the format: "1y2M3w4d5h6m7s8ms"
 * where each component represents a duration unit (years, months, weeks, days, hours, minutes, seconds, milliseconds).
 * Units must appear in order from largest to smallest.
 */
const DURATION_REGEX = new RegExp(
  `^(?:(\\d+)${DURATION_UNIT.YEAR})?(?:(\\d+)${DURATION_UNIT.MONTH})?(?:(\\d+)${DURATION_UNIT.WEEK})?(?:(\\d+)${DURATION_UNIT.DAY})?(?:(\\d+)${DURATION_UNIT.HOUR})?(?:(\\d+)${DURATION_UNIT.MINUTE})?(?:(\\d+)s)?(?:(\\d+)${DURATION_UNIT.MILLISECOND})?$`
);

/**
 * Returns true if the given string is a valid DurationString.
 * Duration strings must have units in order from largest to smallest (y, M, w, d, h, m, s, ms).
 */
export function isDurationString(maybeDuration: string): maybeDuration is DurationString {
  const trimmed = maybeDuration.trim();
  if (trimmed === '') return false;
  return DURATION_REGEX.test(trimmed);
}

/**
 * Parses a DurationString into a Duration object with numeric values that can
 * be used to do Date math. Throws if not a valid duration string.
 */
export function parseDurationString(durationString: string): Duration {
  const matches = DURATION_REGEX.exec(String(durationString).trim());
  if (matches === null) {
    throw new Error(`Invalid duration string '${durationString}'`);
  }

  return {
    years: parseInt(matches[1] ?? '0'),
    months: parseInt(matches[2] ?? '0'),
    weeks: parseInt(matches[3] ?? '0'),
    days: parseInt(matches[4] ?? '0'),
    hours: parseInt(matches[5] ?? '0'),
    minutes: parseInt(matches[6] ?? '0'),
    seconds: parseInt(matches[7] ?? '0') + parseInt(matches[8] ?? '0') / 1000,
  };
}

/**
 * Converts a DurationString to milliseconds.
 */
export function getDurationStringMilliseconds(durationString?: DurationString) {
  if (durationString === undefined || (durationString as string) === '') return undefined;

  const duration = parseDurationString(durationString);
  return milliseconds(duration);
}

/**
 * Converts a DurationString to seconds, rounding down.
 */
export function getDurationStringSeconds(durationString?: DurationString) {
  const durationMs = getDurationStringMilliseconds(durationString);

  if (typeof durationMs !== 'number') {
    return undefined;
  }

  return Math.floor(durationMs / 1000);
}

/**
 * Converts a Duration object into a DurationString.
 */
export function getDurationString(duration: Duration): DurationString | undefined {
  const result =
    `${duration.years ? `${duration.years}${DURATION_UNIT.YEAR}` : ''}` +
    `${duration.months ? `${duration.months}${DURATION_UNIT.MONTH}` : ''}` +
    `${duration.weeks ? `${duration.weeks}${DURATION_UNIT.WEEK}` : ''}` +
    `${duration.days ? `${duration.days}${DURATION_UNIT.DAY}` : ''}` +
    `${duration.hours ? `${duration.hours}${DURATION_UNIT.HOUR}` : ''}` +
    `${duration.minutes ? `${duration.minutes}${DURATION_UNIT.MINUTE}` : ''}` +
    `${duration.seconds ? `${duration.seconds}${DURATION_UNIT.SECOND}` : ''}`;
  if (result === '') return undefined;
  return result as DurationString;
}

/**
 * Determine whether a given time range is relative.
 */
export function isRelativeTimeRange(timeRange: TimeRangeValue): timeRange is RelativeTimeRange {
  return (timeRange as RelativeTimeRange).pastDuration !== undefined;
}

/**
 * Determine whether a given time range is absolute.
 */
export function isAbsoluteTimeRange(timeRange: TimeRangeValue): timeRange is AbsoluteTimeRange {
  return (timeRange as AbsoluteTimeRange).start !== undefined && (timeRange as AbsoluteTimeRange).end !== undefined;
}

/**
 * Converts a RelativeTimeRange to an AbsoluteTimeRange.
 * If no end date is specified, uses the current time.
 */
export function toAbsoluteTimeRange(timeRange: RelativeTimeRange): AbsoluteTimeRange {
  // Set milliseconds to 0 when generating a new date to avoid propagating a somewhat random
  // milliseconds value to the new date that isn't user visible in the UX (e.g. the time picker) and
  // could potentially lead to some weird edge cases.
  const end = timeRange.end ?? setMilliseconds(new Date(), 0);

  return {
    start: sub(end, parseDurationString(timeRange.pastDuration)),
    end,
  };
}

/**
 * Checks whether two time ranges that can be either absolute or relative are equal.
 */
export function areTimeRangesEqual(a: TimeRangeValue, b: TimeRangeValue): boolean {
  if (a === b) return true;

  if (!a || !b) return false;

  if (isRelativeTimeRange(a) && isRelativeTimeRange(b)) {
    return a.pastDuration === b.pastDuration && a.end?.getTime() === b.end?.getTime();
  }

  if (isAbsoluteTimeRange(a) && isAbsoluteTimeRange(b)) {
    const startA = a.start instanceof Date ? a.start : new Date(a.start);
    const endA = a.end instanceof Date ? a.end : new Date(a.end);
    const startB = b.start instanceof Date ? b.start : new Date(b.start);
    const endB = b.end instanceof Date ? b.end : new Date(b.end);
    return startA.getTime() === startB.getTime() && endA.getTime() === endB.getTime();
  }

  // When one time range is relative and the other is absolute, they cannot be equal
  return false;
}

/**
 * Message shown when start date/time is after end date/time.
 */
export const TIME_RANGE_INVALID_ORDER_MESSAGE = 'Range is not valid. Start date and time is after end date and time.';

/**
 * Validates that a time range has valid dates and that start is before end.
 */
export function isValidTimeRange({
  start,
  end,
  isEndOptional,
}: {
  end: Date | null;
  isEndOptional?: boolean;
  start: Date | null;
}): boolean {
  const isStartValid = start !== null && isValid(start);
  const isEndValid = end !== null && isValid(end);

  if (isEndOptional) {
    return isStartValid;
  }
  return isStartValid && isEndValid && isBefore(start, end);
}

/**
 * Takes two date objects and returns a new date with the date from the first and the time from the second.
 * Useful for preserving user-entered times when selecting dates from a calendar.
 *
 * When `timeZone` is set (and not local), the time is taken from `dateWithTime` in that zone and combined
 * with the calendar date, then returned as a UTC date. Use this when the time source is a UTC date
 * (e.g. from interpretDateInTimeZone) and the display timezone is not the browser's.
 */
export function combineDateAndTime(dateWithDate: Date, dateWithTime: Date, timeZone?: string): Date {
  if (isLocalTimeZone(timeZone)) {
    const modifiedDate = new Date(dateWithDate.getTime());
    modifiedDate.setHours(
      dateWithTime.getHours(),
      dateWithTime.getMinutes(),
      dateWithTime.getSeconds(),
      dateWithTime.getMilliseconds()
    );
    return modifiedDate;
  }
  const normalizedTimeZone = normalizeIANATimeZone(timeZone);
  const y = dateWithDate.getFullYear();
  const m = dateWithDate.getMonth();
  const d = dateWithDate.getDate();
  const zonedTime = utcToZonedTime(dateWithTime, normalizedTimeZone);
  const localDate = new Date(
    y,
    m,
    d,
    zonedTime.getHours(),
    zonedTime.getMinutes(),
    zonedTime.getSeconds(),
    zonedTime.getMilliseconds()
  );
  return zonedTimeToUtc(localDate, normalizedTimeZone);
}
